import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, TextInput, Pressable, Platform, ScrollView, ActivityIndicator } from 'react-native'
import { useTheme } from '../../../theme/useTheme'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useSnackbar } from '../../../shared/hooks/useSnackbar'
import { useDialog } from '../../../shared/hooks/useDialog'
import { getAccountSettingsTabStyles } from './AccountSettingsTab.styles'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import { extractErrorMessage } from '../../../../helpers/apiClient.helper'
import { sanitizeInput } from '../../../helpers/sanitizeInput.helper'
import { GROUP_NAME_MAX_LENGTH, GROUP_NAME_MIN_LENGTH } from '../../../friends/constants/groupName'
import { buildOrgDeleteConfirmPhrase } from '../../helpers/orgDeleteConfirmPhrase.helper'
import { ORG_NAME_MAX_LENGTH, ORG_NAME_MIN_LENGTH } from '../../constants/orgForm.constants'
import { sanitizeJoinCodeInput } from '../../helpers/b2bAccount.helper'
import { JOIN_WC_INVITE_CODE_MAX_LENGTH } from '../../../../components/worldcup/JoinWorldCupGroupScreen/JoinWorldCupGroupScreen.constants'
import { flushToPaint } from '../../helpers/flushToPaint.helper'
import type { B2bDefaultWorldCupGroupDto } from '../../types/b2bAccount.types'
interface AccountSettingsTabProps {
  accountName: string
  isAccountActive: boolean
  /** False when sole org admin — must promote another admin before leaving (matches API). */
  canLeaveOrganization: boolean
  linkedWorldCupGroup?: B2bDefaultWorldCupGroupDto | null
  /** Current allowed email domain for the org (null = open org, no restriction). */
  allowedEmailDomain?: string | null
  onSaveName: (name: string) => Promise<void>
  onSaveWorldCupLinkedSquad: (worldCupDefaultSquadInviteCode: string | null) => Promise<void>
  onSaveLinkedWorldCupSquadName: (name: string) => Promise<void>
  onDeactivate: () => Promise<void>
  onReactivate: () => Promise<void>
  onLeaveOrganization: () => Promise<void>
  onDeleteOrganizationPermanently: (confirmDeletionPhrase: string) => Promise<void>
  onSaveSsoSettings: (allowedEmailDomain: string | null) => Promise<void>
  embedInSiteWebOuterScroll?: boolean
}

export const AccountSettingsTab: React.FC<AccountSettingsTabProps> = ({
  accountName,
  isAccountActive,
  canLeaveOrganization,
  linkedWorldCupGroup = null,
  allowedEmailDomain = null,
  onSaveName,
  onSaveWorldCupLinkedSquad,
  onSaveLinkedWorldCupSquadName,
  onDeactivate,
  onReactivate,
  onLeaveOrganization,
  onDeleteOrganizationPermanently,
  onSaveSsoSettings,
  embedInSiteWebOuterScroll = false,
}) => {
  const copy = useAccountAdminCopy()
  const appTheme = useTheme()
  const theme = appTheme
  const styles = useMemo(() => getAccountSettingsTabStyles(theme), [theme])
  const { showSnackbar } = useSnackbar()
  const { showDialog } = useDialog()
  const prevLinkedWorldCupGroupIdRef = useRef<string | undefined>(undefined)
  const [nameDraft, setNameDraft] = useState(accountName)
  const [saving, setSaving] = useState(false)
  const [leaveBusy, setLeaveBusy] = useState(false)
  const [deleteNameDraft, setDeleteNameDraft] = useState('')
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [wcInviteDraft, setWcInviteDraft] = useState('')
  const [wcNameDraft, setWcNameDraft] = useState('')
  const [wcBusy, setWcBusy] = useState(false)
  const [wcNameBusy, setWcNameBusy] = useState(false)
  const [ssoDomainEnabled, setSsoDomainEnabled] = useState(() => Boolean(allowedEmailDomain))
  const [ssoDomainDraft, setSsoDomainDraft] = useState(() => allowedEmailDomain ?? '')
  const [ssoBusy, setSsoBusy] = useState(false)

  useEffect(() => {
    setNameDraft(accountName)
  }, [accountName])

  useEffect(() => {
    const id = linkedWorldCupGroup?.id
    const prev = prevLinkedWorldCupGroupIdRef.current
    prevLinkedWorldCupGroupIdRef.current = id
    if (prev != null && id == null) {
      setWcInviteDraft('')
    }
  }, [linkedWorldCupGroup?.id])

  useEffect(() => {
    setWcNameDraft(linkedWorldCupGroup?.name ?? '')
  }, [linkedWorldCupGroup?.name])

  useEffect(() => {
    setDeleteNameDraft('')
  }, [accountName, isAccountActive])

  useEffect(() => {
    setSsoDomainEnabled(Boolean(allowedEmailDomain))
    setSsoDomainDraft(allowedEmailDomain ?? '')
  }, [allowedEmailDomain])

  const handleSaveSsoDomain = useCallback(async () => {
    const domain = ssoDomainEnabled ? ssoDomainDraft.trim().toLowerCase().replace(/^@/, '') : null
    if (ssoDomainEnabled && (!domain || !domain.includes('.'))) {
      showSnackbar(copy.sso.INVALID_DOMAIN, 'error')
      return
    }
    await flushToPaint(() => setSsoBusy(true))
    try {
      await onSaveSsoSettings(domain ?? null)
      showSnackbar(
        domain ? copy.sso.domainRestricted(domain) : copy.sso.DOMAIN_REMOVED,
        'success',
      )
    } catch (e) {
      showSnackbar(extractErrorMessage(e, copy.sso.SAVE_FAILED), 'error')
    } finally {
      setSsoBusy(false)
    }
  }, [copy.sso, ssoDomainEnabled, ssoDomainDraft, onSaveSsoSettings, showSnackbar])

  const handleSaveName = useCallback(async () => {
    const cleanName = sanitizeInput(nameDraft, true).trim()
    if (cleanName.length < ORG_NAME_MIN_LENGTH || cleanName.length > ORG_NAME_MAX_LENGTH) {
      showSnackbar(
        `Organization name must be ${ORG_NAME_MIN_LENGTH}-${ORG_NAME_MAX_LENGTH} characters`,
        'error',
      )
      return
    }
    await flushToPaint(() => setSaving(true))
    try {
      await onSaveName(cleanName)
      showSnackbar('Saved', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Save failed'), 'error')
    } finally {
      setSaving(false)
    }
  }, [nameDraft, onSaveName, showSnackbar])

  const handleChangeWcInviteDraft = useCallback((text: string) => {
    const sanitized = sanitizeInput(text, true).slice(0, JOIN_WC_INVITE_CODE_MAX_LENGTH)
    setWcInviteDraft(sanitized.toUpperCase())
  }, [])

  const handleSaveWorldCupLinkedSquad = useCallback(async () => {
    const trimmed = wcInviteDraft.trim()
    const compact = trimmed.length === 0 ? '' : sanitizeJoinCodeInput(trimmed)
    await flushToPaint(() => setWcBusy(true))
    try {
      await onSaveWorldCupLinkedSquad(compact.length === 0 ? null : compact)
      showSnackbar('Saved', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Could not update squad link'), 'error')
    } finally {
      setWcBusy(false)
    }
  }, [wcInviteDraft, onSaveWorldCupLinkedSquad, showSnackbar])

  const handleChangeWcNameDraft = useCallback((text: string) => {
    const sanitized = sanitizeInput(text, true)
    setWcNameDraft(sanitized.slice(0, GROUP_NAME_MAX_LENGTH))
  }, [])

  const handleSaveLinkedSquadName = useCallback(async () => {
    const t = wcNameDraft.trim()
    if (t.length < GROUP_NAME_MIN_LENGTH) {
      showSnackbar(`Squad name must be at least ${GROUP_NAME_MIN_LENGTH} characters`, 'error')
      return
    }
    await flushToPaint(() => setWcNameBusy(true))
    try {
      await onSaveLinkedWorldCupSquadName(t)
      showSnackbar('Squad name saved', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Could not save squad name'), 'error')
    } finally {
      setWcNameBusy(false)
    }
  }, [wcNameDraft, onSaveLinkedWorldCupSquadName, showSnackbar])

  const handleReactivate = useCallback(async () => {
    try {
      await onReactivate()
      showSnackbar('Reactivated', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Reactivate failed'), 'error')
    }
  }, [onReactivate, showSnackbar])

  const handleDeactivate = useCallback(async () => {
    const ok = await showDialog({
      title: copy.DEACTIVATE_CONFIRM_TITLE,
      message: copy.DEACTIVATE_CONFIRM_MESSAGE,
      type: 'danger',
      confirmText: copy.DEACTIVATE,
      cancelText: 'Cancel',
    })
    if (!ok) return
    try {
      await onDeactivate()
      showSnackbar('Organization deactivated', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Could not deactivate'), 'error')
    }
  }, [onDeactivate, showDialog, showSnackbar])

  const handleLeaveOrganization = useCallback(async () => {
    const ok = await showDialog({
      title: copy.LEAVE_ORG_TITLE,
      message: copy.LEAVE_ORG_MESSAGE,
      type: 'danger',
      confirmText: copy.LEAVE_ORG_CONFIRM,
      cancelText: 'Cancel',
    })
    if (!ok) return
    await flushToPaint(() => setLeaveBusy(true))
    try {
      await onLeaveOrganization()
      showSnackbar(copy.LEAVE_ORG_DONE_TITLE, 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Could not leave'), 'error')
    } finally {
      setLeaveBusy(false)
    }
  }, [onLeaveOrganization, showDialog, showSnackbar])

  const requiredDeletePhrase = useMemo(() => buildOrgDeleteConfirmPhrase(accountName), [accountName])
  const deleteNameMatches = deleteNameDraft.trim() === requiredDeletePhrase

  const handleDeleteOrganizationPermanently = useCallback(async () => {
    if (!deleteNameMatches) return
    const ok = await showDialog({
      title: copy.DELETE_ORG_DIALOG_TITLE,
      message: copy.DELETE_ORG_DIALOG_MESSAGE,
      type: 'danger',
      confirmText: copy.DELETE_ORG_BUTTON,
      cancelText: 'Cancel',
    })
    if (!ok) return
    await flushToPaint(() => setDeleteBusy(true))
    try {
      await onDeleteOrganizationPermanently(deleteNameDraft.trim())
      showSnackbar('Organization deleted', 'success')
    } catch (e) {
      showSnackbar(extractErrorMessage(e, 'Could not delete'), 'error')
    } finally {
      setDeleteBusy(false)
    }
  }, [
    deleteNameDraft,
    deleteNameMatches,
    onDeleteOrganizationPermanently,
    showDialog,
    showSnackbar,
  ])

  const settingsBody = (
    <>
      {!isAccountActive ? (
        <>
          <View style={[styles.section, styles.sectionFlushBottom]}>
            <Text style={styles.dangerTitle}>{copy.INACTIVE_TITLE}</Text>
            <Text style={styles.body}>{copy.INACTIVE_BODY}</Text>
            <Pressable
              accessibilityRole="button"
              onPress={() => void handleReactivate()}
              style={({ pressed, hovered }) => [
                styles.primaryButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.primaryButtonPressed,
                hovered && Platform.OS === 'web' && styles.primaryButtonHover,
              ]}
            >
              <Text style={styles.primaryButtonText}>{copy.REACTIVATE}</Text>
            </Pressable>
          </View>
          <View
            style={styles.sectionDivider}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          />
        </>
      ) : null}

      <View style={[styles.section, styles.sectionFlushBottom]}>
        <Text style={styles.title}>{copy.ACCOUNT_NAME}</Text>
        <TextInput
          value={nameDraft}
          onChangeText={(text) => setNameDraft(sanitizeInput(text, true).slice(0, ORG_NAME_MAX_LENGTH))}
          style={styles.input}
          placeholderTextColor={theme.gray[500]}
        />
        <Pressable
          accessibilityRole="button"
          disabled={saving}
          onPress={() => void handleSaveName()}
          style={({ pressed, hovered }) => [
            styles.primaryButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.primaryButtonPressed,
            hovered && Platform.OS === 'web' && styles.primaryButtonHover,
            saving && styles.primaryButtonDisabled,
          ]}
        >
          {saving ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
          <Text style={styles.primaryButtonText}>{copy.SAVE_NAME}</Text>
        </Pressable>
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      {/* SSO domain restriction */}
      <View style={[styles.section, styles.sectionFlushBottom]}>
        <Text style={styles.title}>{copy.sso.TITLE}</Text>
        <Text style={styles.body}>{copy.sso.BODY}</Text>

        <Pressable
          accessibilityRole="checkbox"
          accessibilityState={{ checked: ssoDomainEnabled }}
          onPress={() => setSsoDomainEnabled((v) => !v)}
          style={({ pressed }) => [
            styles.ssoCheckboxRow,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && { opacity: 0.85 },
          ]}
        >
          <View
            style={[
              styles.ssoCheckboxBox,
              {
                borderColor: ssoDomainEnabled ? theme.primary : theme.gray[500],
                backgroundColor: ssoDomainEnabled ? theme.primary : 'transparent',
              },
            ]}
          >
            {ssoDomainEnabled ? <Text style={{ color: '#fff', fontSize: 12, fontWeight: '700' }}>✓</Text> : null}
          </View>
          <Text style={styles.ssoCheckboxLabel}>{copy.sso.CHECKBOX_LABEL}</Text>
        </Pressable>

        {ssoDomainEnabled ? (
          <TextInput
            value={ssoDomainDraft}
            onChangeText={(t) => setSsoDomainDraft(t.trim().toLowerCase().replace(/^@/, ''))}
            style={[styles.input, { marginTop: 8 }]}
            placeholder={copy.sso.DOMAIN_PLACEHOLDER}
            placeholderTextColor={theme.gray[500]}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="email-address"
            editable={!ssoBusy}
          />
        ) : null}

        <Pressable
          accessibilityRole="button"
          disabled={ssoBusy}
          onPress={() => void handleSaveSsoDomain()}
          style={({ pressed, hovered }) => [
            styles.primaryButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.primaryButtonPressed,
            hovered && Platform.OS === 'web' && styles.primaryButtonHover,
            ssoBusy && styles.primaryButtonDisabled,
            { marginTop: 8 },
          ]}
        >
          {ssoBusy ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
          <Text style={styles.primaryButtonText}>{copy.sso.SAVE_BUTTON}</Text>
        </Pressable>
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      <View style={[styles.section, styles.sectionFlushBottom]}>
        <Text style={styles.title}>{copy.WC_LINKED_SQUAD_SECTION_TITLE}</Text>
        <Text style={styles.body}>
          {linkedWorldCupGroup
            ? copy.WC_LINKED_SQUAD_SECTION_BODY_LINKED
            : copy.WC_LINKED_SQUAD_SECTION_BODY_NOT_LINKED}
        </Text>
        {linkedWorldCupGroup ? (
          <>
            <Text style={styles.wcFieldLabel}>{copy.WC_LINKED_SQUAD_NAME_LABEL}</Text>
            <TextInput
              value={wcNameDraft}
              onChangeText={handleChangeWcNameDraft}
              style={styles.input}
              placeholderTextColor={theme.gray[500]}
              maxLength={GROUP_NAME_MAX_LENGTH}
              autoCorrect={false}
            />
            <Pressable
              accessibilityRole="button"
              disabled={wcNameBusy}
              onPress={() => void handleSaveLinkedSquadName()}
              style={({ pressed, hovered }) => [
                styles.primaryButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.primaryButtonPressed,
                hovered && Platform.OS === 'web' && styles.primaryButtonHover,
                wcNameBusy && styles.primaryButtonDisabled,
              ]}
            >
              {wcNameBusy ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
              <Text style={styles.primaryButtonText}>{copy.WC_LINKED_SQUAD_SAVE_NAME}</Text>
            </Pressable>
          </>
        ) : null}
        {!linkedWorldCupGroup ? (
          <>
            <Text style={styles.wcFieldLabel}>{copy.WC_LINKED_SQUAD_INVITE_CODE_LABEL}</Text>
            <TextInput
              value={wcInviteDraft}
              onChangeText={handleChangeWcInviteDraft}
              style={styles.input}
              placeholder={copy.WC_LINKED_SQUAD_CODE_PLACEHOLDER}
              placeholderTextColor={theme.gray[500]}
              autoCapitalize="characters"
              autoCorrect={false}
            />
            <Pressable
              accessibilityRole="button"
              disabled={wcBusy}
              onPress={() => void handleSaveWorldCupLinkedSquad()}
              style={({ pressed, hovered }) => [
                styles.primaryButton,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.primaryButtonPressed,
                hovered && Platform.OS === 'web' && styles.primaryButtonHover,
                wcBusy && styles.primaryButtonDisabled,
              ]}
            >
              {wcBusy ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
              <Text style={styles.primaryButtonText}>{copy.WC_LINKED_SQUAD_LINK_SQUAD}</Text>
            </Pressable>
          </>
        ) : null}
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      <View style={styles.leaveSection}>
        <Text style={styles.leaveTitle}>{copy.LEAVE_ORG_CONFIRM}</Text>
        <Text style={styles.body}>{copy.LEAVE_ORG_MESSAGE}</Text>
        {!canLeaveOrganization ? (
          <Text style={styles.leaveSoleAdminHint}>{copy.LEAVE_ORG_SOLE_ADMIN_HINT}</Text>
        ) : null}
        <Pressable
          accessibilityRole="button"
          disabled={leaveBusy || !canLeaveOrganization}
          onPress={() => void handleLeaveOrganization()}
          style={({ pressed, hovered }) => [
            styles.leaveOutlineButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && canLeaveOrganization && styles.leaveOutlineButtonPressed,
            hovered && Platform.OS === 'web' && canLeaveOrganization && styles.leaveOutlineButtonHover,
            (leaveBusy || !canLeaveOrganization) && styles.leaveOutlineButtonDisabled,
          ]}
        >
          {leaveBusy ? <ActivityIndicator size="small" color={theme.primary} style={{ marginRight: 6 }} /> : null}
          <Text
            style={[
              styles.leaveOutlineButtonText,
              !canLeaveOrganization && styles.leaveOutlineButtonTextDisabled,
            ]}
          >
            {copy.LEAVE_ORG_CONFIRM}
          </Text>
        </Pressable>
      </View>

      <View
        style={styles.sectionDivider}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />

      {!isAccountActive ? (
        <View style={styles.deletePermanentSection}>
          <Text style={styles.dangerTitle}>{copy.DELETE_ORG_SECTION_TITLE}</Text>
          <Text style={styles.deletePermanentIntro}>{copy.DELETE_ORG_SECTION_BODY}</Text>
          <Text style={styles.deletePermanentLabel}>{copy.DELETE_ORG_NAME_LABEL}</Text>
          <Text style={styles.deletePermanentExpectedName}>{requiredDeletePhrase}</Text>
          <TextInput
            value={deleteNameDraft}
            onChangeText={setDeleteNameDraft}
            style={styles.deletePermanentInput}
            placeholder={requiredDeletePhrase}
            placeholderTextColor={theme.gray[500]}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <Pressable
            accessibilityRole="button"
            disabled={!deleteNameMatches || deleteBusy}
            onPress={() => void handleDeleteOrganizationPermanently()}
            style={({ pressed }) => [
              styles.dangerButton,
              styles.deletePermanentSubmit,
              pressed && styles.deletePermanentDangerPressed,
              (!deleteNameMatches || deleteBusy) && styles.deletePermanentDangerDisabled,
            ]}
          >
            {deleteBusy ? <ActivityIndicator size="small" color={theme.white} style={{ marginRight: 6 }} /> : null}
            <Text style={styles.dangerButtonText}>{copy.DELETE_ORG_BUTTON}</Text>
          </Pressable>
        </View>
      ) : null}

      {isAccountActive ? (
        <View style={styles.dangerZone}>
          <Text style={styles.dangerTitle}>{copy.DEACTIVATE}</Text>
          <Text style={styles.body}>{copy.DEACTIVATE_CONFIRM_MESSAGE}</Text>
          <Text style={styles.body}>{copy.DEACTIVATE_PERMANENT_DELETE_FOOTNOTE}</Text>
          <Pressable
            accessibilityRole="button"
            onPress={() => void handleDeactivate()}
            style={({ pressed }) => [styles.dangerButton, pressed && styles.dangerButtonPressed]}
          >
            <Text style={styles.dangerButtonText}>{copy.DEACTIVATE}</Text>
          </Pressable>
        </View>
      ) : null}
    </>
  )

  if (embedInSiteWebOuterScroll) {
    return (
      <View style={[styles.embeddedBody, styles.settingsScrollContent]}>{settingsBody}</View>
    )
  }

  return (
    <ScrollView
      style={styles.settingsScroll}
      contentContainerStyle={styles.settingsScrollContent}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={Platform.OS !== 'web'}
    >
      {settingsBody}
    </ScrollView>
  )
}
